import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../context/FeedbackContext';
import { feedbackApi } from '../../services/api';
import './FeedbackFab.css';

const HIDDEN_ROUTES = ['/login', '/auth/callback', '/logout/callback'];

export interface FeedbackFabProps {
  /** Posicao do botao. Padrao: 'bottom-right'. */
  position?: 'bottom-right' | 'bottom-left';
  /** Texto do tooltip. Padrao: 'Deixe seu feedback'. */
  tooltipLabel?: string;
}

/**
 * Floating Action Button (FAB) de feedback do usuario.
 *
 * Boas praticas aplicadas:
 * - Posicionamento fixo no canto inferior direito (padrao da industria).
 * - Tooltip apresentado automaticamente no primeiro hover/focus.
 * - Pulso sutil quando o usuario esta elegivel (cadencia vencida) - chama atencao
 *   sem ser intrusivo. Animacao desligada via `prefers-reduced-motion`.
 * - Acessivel: <button> nativo, `aria-label`, `aria-haspopup="dialog"`,
 *   `aria-pressed` quando o modal esta aberto, focusable via Tab.
 * - Visibilidade controlada: so aparece para usuarios autenticados e fora das
 *   rotas de login/callback (HIDDEN_ROUTES).
 * - Sem memory leak: AbortController na consulta de elegibilidade,
 *   isMountedRef previne setState pos-desmonte.
 */
const FeedbackFab: React.FC<FeedbackFabProps> = memo(({
  position = 'bottom-right',
  tooltipLabel = 'Deixe seu feedback',
}) => {
  const { isAuthenticated } = useAuth();
  const { requestFeedback } = useFeedback();
  const { pathname } = useLocation();

  const [isEligible, setIsEligible] = useState<boolean>(false);
  const [isOpening, setIsOpening] = useState<boolean>(false);

  const isMountedRef = useRef<boolean>(true);

  const isHidden = HIDDEN_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
  const shouldRender = isAuthenticated && !isHidden;

  // Consulta a cadencia uma vez por sessao/route-change para indicar visualmente
  // (pulso) quando o usuario "deveria" deixar feedback. Falha silenciosamente.
  useEffect(() => {
    if (!shouldRender) return;
    isMountedRef.current = true;
    const controller = new AbortController();

    feedbackApi
      .shouldPrompt(null, controller.signal)
      .then((result) => {
        if (!isMountedRef.current) return;
        setIsEligible(Boolean(result.should_prompt));
      })
      .catch(() => {
        // Falha silenciosa: nao impede o usuario de clicar no FAB manualmente.
      });

    return () => {
      isMountedRef.current = false;
      controller.abort();
    };
  }, [shouldRender, pathname]);

  const handleClick = useCallback(async () => {
    if (isOpening) return;
    setIsOpening(true);
    try {
      await requestFeedback({ force: true });
      if (isMountedRef.current) setIsEligible(false);
    } finally {
      if (isMountedRef.current) setIsOpening(false);
    }
  }, [isOpening, requestFeedback]);

  if (!shouldRender) return null;

  const positionClass = position === 'bottom-left' ? 'is-bottom-left' : 'is-bottom-right';
  const stateClass = isEligible ? 'is-eligible' : '';

  const tooltip = (
    <Tooltip id="feedback-fab-tooltip" className="feedback-fab__tooltip">
      {tooltipLabel}
      {isEligible && (
        <div className="feedback-fab__tooltip-hint">
          Que tal avaliar a plataforma agora?
        </div>
      )}
    </Tooltip>
  );

  return (
    <OverlayTrigger
      placement={position === 'bottom-left' ? 'right' : 'left'}
      overlay={tooltip}
      delay={{ show: 250, hide: 100 }}
    >
      <button
        type="button"
        className={`feedback-fab ${positionClass} ${stateClass}`}
        onClick={handleClick}
        aria-label={tooltipLabel}
        aria-haspopup="dialog"
      >
        <span className="feedback-fab__icon" aria-hidden="true">
          <i className="bi bi-chat-heart-fill"></i>
        </span>
        {isEligible && (
          <span className="feedback-fab__badge" aria-hidden="true"></span>
        )}
      </button>
    </OverlayTrigger>
  );
});

FeedbackFab.displayName = 'FeedbackFab';

export default FeedbackFab;
